import { Entity } from '@backstage/catalog-model';
import { EntityLayout } from '@backstage/plugin-catalog';
import { EntityProvider } from '@backstage/plugin-catalog-react';
import {
  decryptAllFilesList,
  fetchData,
  getPrivateKeyByEmail,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import { LinearProgress, Paper, TableContainer } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { makeStyles } from '@material-ui/styles';
import Drawer from '@mui/material/Drawer';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdrList from '../../components/Adr';
import AdrContent from '../../components/Adr/content';
import { getAdrContentBytitle } from '../../services/adr.service';
import GuardRailTags from './GuardRail-Tags';
import { guardRailsProps } from './types';
import useCustomEntities from '../../hooks/useEntities';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
const sdlcOrder = [
  'Plan',
  'Code',
  'Build',
  'Test',
  'Release',
  'Deploy',
  'Operate',
  'Monitor',
];

const useStyles = makeStyles(() => ({
  NavHamburger: {
    backgroundColor: '#fff',
    height: '50px',
    padding: '15px',
    border: 'none',
    borderRadius: '5px',
    width: '60px',
    '&:hover': {
      cursor: 'pointer', // Change the cursor to pointer on hover
    },
  },
}));

function sdlcProcessData(textData: string) {
  let sdlc: string = '';
  let title: string = '';
  let status: string = '';
  const tags: string[] = [];

  try {
    // Regular expressions for metadata extraction
    const sdlcPattern = /^\s*sdlc:\s*(.*)$/gim;
    const titlePattern = /^\s*title:\s*(.*)$/gim;
    const statusPattern = /^\s*status:\s*(.*)$/gim;
    const tagsPattern = /^\s*tags:\s*$/gim;
    const tagItemPattern = /^\s*-\s*(.*)$/gim;

    // Split textData by lines and process each line
    const lines = textData.split('\n');
    let insideTagsSection = false;

    lines.forEach(line => {
      // Extract data based on matching patterns
      const sdlcMatch = sdlcPattern.exec(line);
      const titleMatch = titlePattern.exec(line);
      const statusMatch = statusPattern.exec(line);
      const tagsMatch = tagsPattern.exec(line);
      const tagItemMatch = tagItemPattern.exec(line);

      if (sdlcMatch) {
        sdlc = sdlcMatch[1]?.trim();
      } else if (titleMatch) {
        title = titleMatch[1]?.trim();
      } else if (statusMatch) {
        status = statusMatch[1]?.trim();
      } else if (tagsMatch) {
        insideTagsSection = true; // Start of the tags section
      } else if (insideTagsSection && tagItemMatch) {
        tags.push(tagItemMatch[1]?.trim());
      } else if (insideTagsSection && line.trim() === '') {
        insideTagsSection = false; // End of the tags section when encountering an empty line
      }

      // Reset regex state for the next iteration
      sdlcPattern.lastIndex = 0;
      titlePattern.lastIndex = 0;
      statusPattern.lastIndex = 0;
      tagsPattern.lastIndex = 0;
      tagItemPattern.lastIndex = 0;
    });

    return {
      title,
      sdlc,
      status,
      tags,
    };
  } catch (error) {
    return null;
  }
}

const staticEntity = {
  apiVersion: 'backstage.io/v1alpha1',
  kind: '',
  metadata: {
    name: 'Guardrails',
    subtitle: 'A brief description or subtitle here',
  },
};

const hasEncodedOrSpecial = (name: string) => {
  const hasEncoded = /%[0-9A-Fa-f]{2}/.test(name);

  const hasSpecial = /[^a-zA-Z0-9. ]/.test(name);

  return hasEncoded || hasSpecial;
};

const normalizeFilename = (name: string) => {
  if (!hasEncodedOrSpecial(name)) {
    return name;
  }

  const decoded = decodeURIComponent(name);
  const data = decoded
    .replace(/[^a-zA-Z0-9.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return data;
};

const GuardRails: FC<{
  setIsLoading?: (isLoading: boolean) => void;
  url?: string;
}> = ({ setIsLoading, url }) => {
  const splitLocation = window.location.pathname.split('/');
  const path_ = window.location.pathname;
  const { email } = useSelector((state: RootState) => state.auth);

  const entityName = encodeURIComponent(
    window.location.pathname?.split('/')[4],
  );
  document.title = 'Guardrails';
  const classes = useStyles();

  const isGuardRailList =
    window.location.pathname.split('/')[1] === 'guardrails';
  const [guardRailState, setGuardRailState] = useState<guardRailsProps>({
    cops: '',
    guardRailList: [],
    sdlc: 'Build',
    status: '',
    adrContent: '',
    tags: '',
    title: '',
    path: '',
    isLoading: true,
    selected: '',
    guardRailLocationPath: '',
    error: null,
    isGuardRailAvailable: false,
    sdlcProcessed: [],
    isNavOpen: false,
    selectedTag: '',
    tagList: [],
    baseUrl: '',
  });

  const navigate = useNavigate();
  const displayedPhases = new Set();
  let name: string = '';
  name = !isGuardRailList ? entityName : '';

  const path = window.location.pathname?.split('/')[2];

  const { getCustomEntitiesByKind } = useCustomEntities('Guardrail');
  const handleGuardRailClick = (title: string, url?: string) => {
    
    navigate(title);
    fetchFileContentByPath(title,Array.isArray(url)?url[0]:  !url?.endsWith('/')?url?.concat('/'):(Array.isArray(url)?url[0]:url));
    setGuardRailState(prev => ({
      ...prev,
      selected: title,
    }));
    return;
  };

  const fetchFileContentByPath = useCallback(
    async (title: string, url?: string) => {
      
      
      let fileContent: { data: string } = {
        data: '',
      };
      fileContent = await getAdrContentBytitle(url!!?.replace('url:','')?.concat(title));

      setGuardRailState(prev => ({
        ...prev,
        adrContent: fileContent?.data,
      }));

      return fileContent;
    },
    [guardRailState.guardRailLocationPath, isGuardRailList, path],
  );

  const destructureGuardRailByResponse = useCallback(
    async (fileList: string[], processedEntity: Entity[]|string) => {
      const processedData_: [] = [];
      for (const file of fileList as any) {
        const { path } = file;
        const baseUrl = Array.isArray(processedEntity)?processedEntity.find(
          x =>
            normalizeFilename(
              x?.metadata?.annotations?.['filename'] ?? '',
            )?.toLowerCase() === normalizeFilename(path.toLowerCase()),
        )?.metadata?.annotations?.[
          'dock.dh.lufthansa-group.com/guardrail-location'
        ]:processedEntity?.replace('url:','');
        const response = await getAdrContentBytitle(
         baseUrl?.concat( typeof processedEntity==='string'?'/'.concat(path):path) as string,
        );
        const processData = sdlcProcessData(response?.data);
        processedData_.push({ ...processData, path, baseUrl } as never);
      }
      processedData_.sort((a: any, b: any) => {
        return (
          sdlcOrder.indexOf(a.sdlc.replace(/\"/g, '')) -
          sdlcOrder.indexOf(b.sdlc.replace(/\"/g, ''))
        );
      });

      return processedData_;
    },
    [],
  );

  // async function GetData(processedEntity: Entity | Entity[]|string) {
  //   if (processedEntity && Array.isArray(processedEntity) || typeof processedEntity==='string') {
  //     const location = typeof processedEntity ==='object'? processedEntity
  //       .map(item => {
  //         return item?.metadata?.annotations?.[
  //           'dock.dh.lufthansa-group.com/guardrail-location'
  //         ]?.replaceAll('url:', '') as string;
  //       })
  //       .filter(Boolean):processedEntity;

  //     let responses: any;
  //     responses = await Promise.all(
  //       location
  //         .filter((item, index, self) => {
  //           return self.findIndex(x => x === item) === index;
  //         })
  //         .map(item => fetchData(`${urls.getAdrList}${item}`)),
  //     );

  //       const privateKey=await getPrivateKeyByEmail(email)

  //       const responseListJoin = responses
  //       .map((item: { data: [] }) => {
  //         return item?.data;
  //       })
  //       .flat();

  //      const decryptedFiles = await decryptAllFilesList(responseListJoin, privateKey);
  //     try {
  //       const allFiles = decryptedFiles
  //         // keep only items that have a status
  //         .filter((x: { status: string }) => x.status)

  //         // remove duplicates by title
  //         .filter(
  //           (item: { title: string }, index: number, self: any[]) =>
  //             self.findIndex(y => y.title === item.title) === index,
  //         )
  //         .filter((j: { path: string }) =>
  //           processedEntity.some(
  //             path =>
  //               normalizeFilename(
  //                 path.metadata.annotations?.['filename'] ?? '',
  //               ).toLowerCase() === normalizeFilename(j.path.toLowerCase()),
  //           ),
  //         );

  //       return { files: allFiles, location: location };
  //     } catch (err) {
  //       console.error('Error processing guard rails:', err);
  //     }
  //   }
  //   return [];
  // }
  async function GetData(processedEntity: Entity | Entity[] | string) {
    
    if (!processedEntity) return [];

    const entitiesArray = Array.isArray(processedEntity)
      ? processedEntity
      : [processedEntity];

    // Extract locations
    const location =
      typeof entitiesArray[0] === 'object'
        ? entitiesArray
            .map((item: any) =>
              item?.metadata?.annotations?.[
                'dock.dh.lufthansa-group.com/guardrail-location'
              ]?.replaceAll('url:', ''),
            )
            .filter(Boolean)
        : (entitiesArray as string[]);

    try {
      const uniqueLocations = Array.from(new Set(location));
      const responses = await Promise.all(
        uniqueLocations.map(item =>
          fetchData(`${urls.getAdrList}${item?.replace('url:', '')}`),
        ),
      );

      const privateKey = await getPrivateKeyByEmail(email);

      const responseListJoin = responses.flatMap(item => item?.data ?? []);

      const decryptedFiles = await decryptAllFilesList(
        responseListJoin,
        privateKey,
      );

      const allFiles = decryptedFiles
        .filter((x: { status: string }) => x.status)
        .filter(
          (item: { title: string }, index: number, self: any[]) =>
            self.findIndex(y => y.title === item.title) === index,
        )
        // .filter((j: { path: string }) =>
        //   entitiesArray.some(path =>
        //     typeof path === 'object'
        //       ? normalizeFilename(
        //           path.metadata.annotations?.['filename'] ?? '',
        //         ).toLowerCase() === normalizeFilename(j.path.toLowerCase())
        //       : normalizeFilename(j.path.toLowerCase()) ===
        //         normalizeFilename(path.toLowerCase()),
        //   ),
        // );

      return { files: allFiles, location: uniqueLocations };
    } catch (err) {
      console.error('Error processing guard rails:', err);
      return [];
    }
  }

  const getGuardRailsByUrlList = useCallback(
    async (processedEntity?: Entity[] | Entity | any) => {
      
      
      try {
        const { files, location } = (await GetData(
          processedEntity as Entity,
        )) as any;

        const destructuredData = (await destructureGuardRailByResponse(
          files as string[],
          processedEntity,
        )) as any;

        

        const flatData = destructuredData
          .flat()
          .filter((x: { path: string }) => x.path !== '') as any;


        if (
          (Array.isArray(processedEntity) || typeof processedEntity==='string') &&
          !path_?.toLowerCase().endsWith('.md')
        ) {
          handleGuardRailClick(files[0]?.path, !location[0]?.endsWith('/')?location[0]?.replace('url:','').concat('/'):location);

          setIsLoading?.(false);
          setGuardRailState(prev => ({
            ...prev,
            error: '',
            guardRailList: flatData,
            tagList: flatData,
            isLoading: false,
            baseUrl: location,
          }));
        }
        else if (path_?.toLowerCase().endsWith('.md')) {
          const segmentRaw =
            path_.split('/')[6]?.toLowerCase() ||
            path_.split('/')[2]?.toLowerCase();
          // decode first so %20 becomes a space
          const segment = decodeURIComponent(segmentRaw);

          if(Array.isArray(processedEntity)){
            const loc =   processedEntity?.find(
            (x: any) =>
              normalizeFilename(
                x.metadata.annotations?.['filename'] ?? '',
              )?.toLowerCase() === normalizeFilename(segment),
          ) as Entity;
          const { filename } = loc.metadata.annotations as any;

          handleGuardRailClick(
            filename,
            loc.metadata.annotations?.[
              'dock.dh.lufthansa-group.com/guardrail-location'
            ],
          );
          }
          else{
            const fileName=window.location.pathname.split('/')[6]

           

          handleGuardRailClick(
            fileName,
            location[0]?.replace('url:',''),
          );
          }
          
          setIsLoading?.(false);
          setGuardRailState(prev => ({
            ...prev,
            error: '',
            guardRailList: flatData,
            tagList: flatData,
            isLoading: false,
            baseUrl: location,
          }));
        }
      } catch (error) {
        if (error instanceof Error) {
          setGuardRailState(prev => ({
            ...prev,
            error: 'an error occured',
            isLoading: false,
          }));
        }
      }
    },
    [name, destructureGuardRailByResponse, isGuardRailList],
  );

  const getEntity = useCallback(async function getEntity() {
    
    const customEntity = (await getCustomEntitiesByKind()).items;
    if (splitLocation[4]) {
      const entity_ = customEntity?.filter(x => {
        const owner = x.spec?.owner;
        return (
          owner?.toString().toLowerCase() === splitLocation[4]?.toLowerCase()
        );
      }) as Entity[];
      getGuardRailsByUrlList(entity_);
    } else {
      getGuardRailsByUrlList(customEntity);
    }
  }, []);

  useEffect(() => {
    if (!url) getEntity();
    else getGuardRailsByUrlList(url);
  }, [getEntity]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const elements = document.querySelectorAll('.css-wf16b5');

      if (elements.length > 0) {
        // Iterate over each element and change its margin
        elements.forEach(element => {
          const htmlElement = element as HTMLElement; // Cast to HTMLElement
          htmlElement.style.width = '300px'; // New width value
        });
        // Clear the interval once the elements are found and styles applied
        clearInterval(intervalId);
      }
    }, 100); // Check every 100ms

    // Cleanup the interval if the component unmounts
    return () => clearInterval(intervalId);
  }, [guardRailState.isNavOpen]); // Dependency array contains the variable

  const getUniqueGuardRailHeader = (sdlc: string) => {
    if (!displayedPhases.has(sdlc)) {
      displayedPhases.add(sdlc);
      return sdlc;
    }
    return null;
  };

  const handleNav = () => {
    const { isNavOpen } = guardRailState;
    setGuardRailState(prev => ({
      ...prev,
      isNavOpen: isNavOpen ? false : true,
    }));
  };

  const getTagFilterList = () => {
    const list = guardRailState.guardRailList.map(item => {
      return item.tags;
    });
    const sortedList = list.flat().filter((item, index, self) => {
      return self.indexOf(item) === index && item !== '--';
    });
    return sortedList;
  };

  const onFilter = (tag: string) => {
    const { guardRailList, tagList } = guardRailState;
    if (tag) {
      const filteredList = guardRailList.filter(x => x.tags?.includes(tag));
      setGuardRailState(prev => ({
        ...prev,
        selectedTag: tag,
        isNavOpen: false,
        guardRailList: filteredList,
      }));
    } else {
      setGuardRailState(prev => ({
        ...prev,
        selectedTag: tag,
        guardRailList: tagList as any,
        isNavOpen: false,
      }));
    }
  };

  const GuardRailsView = () => {
    return (
      <div
        style={{
          top: isGuardRailList ? '170px' : '190px',
          minHeight: '10vh',
        }}
      >
        {!guardRailState.isLoading &&
          !guardRailState.error &&
          guardRailState.guardRailList.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '20px',
              }}
            >
              {isGuardRailList && (
                <div
                  onClick={handleNav}
                  className={classes.NavHamburger}
                  role="button"
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleNav();
                    }
                  }}
                  tabIndex={0}
                >
                  <FilterListIcon />
                </div>
              )}
              <Drawer
                anchor="left"
                open={guardRailState.isNavOpen}
                onClose={handleNav}
              >
                <GuardRailTags
                  filterList={getTagFilterList()}
                  onFilter={onFilter}
                  selected={guardRailState.selectedTag}
                />
              </Drawer>
              <TableContainer
                component={Paper}
                style={{ width: '35rem', padding: '2rem' }}
              >
                {guardRailState.guardRailList.map((guardrail, index) => {
                  let header: string | null = '';
                  // Determine the header based on the sdlc field
                  if (guardrail.sdlc) {
                    header = getUniqueGuardRailHeader(guardrail.sdlc);
                  }

                  // Return the rendered component with a unique key for each item
                  return (
                    <React.Fragment
                      key={index.toLocaleString().concat(guardrail.sdlc)}
                    >
                      <h1>{header?.replace(/"/g, '')}</h1>
                      <AdrList
                        name={guardrail.title}
                        title={guardrail.title}
                        path={guardrail.path}
                        status={guardrail.status}
                        selected={guardRailState.selected}
                        onClick={() =>
                          handleGuardRailClick(
                            guardrail.path,
                            guardrail.baseUrl?.endsWith('//')
                              ? guardrail.baseUrl.slice(0, -1)
                              : guardrail.baseUrl,
                          )
                        }
                      />
                    </React.Fragment>
                  );
                })}
              </TableContainer>
              <AdrContent
                baseUrl={guardRailState.guardRailLocationPath}
                retrievedContent={guardRailState.adrContent}
              />
            </div>
          )}
      </div>
    );
  };

  if (window.location.pathname.split('/')[1] === 'catalog') {
    return <GuardRailsView />;
  }

  return (
    <>
      <EntityProvider entity={staticEntity}>
        <EntityLayout>
          <EntityLayout.Route path="/guardrail" title="Overview">
            {guardRailState.guardRailList &&
            guardRailState.guardRailList.length > 0 ? (
              <GuardRailsView />
            ) : (
              <LinearProgress />
            )}
          </EntityLayout.Route>
        </EntityLayout>
      </EntityProvider>
    </>
  );
};

export default GuardRails;
